"use client";

import { useRef, useState, useEffect, type ReactNode, type RefObject } from "react";
import Link from "next/link";

/* ─── Scroll reveal ───────────────────────────────────────── */
function useVisible<T extends HTMLElement = HTMLDivElement>(threshold = 0.1): [RefObject<T>, boolean] {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } },
      { threshold }
    );
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function Reveal({ children, delay = 0, from = "bottom" }: { children: ReactNode; delay?: number; from?: "bottom" | "left" | "right" }) {
  const [ref, v] = useVisible();
  const hidden: Record<"bottom" | "left" | "right", string> = {
    bottom: "opacity-0 translate-y-7",
    left:   "opacity-0 -translate-x-7",
    right:  "opacity-0 translate-x-7",
  };
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${v ? "opacity-100 translate-x-0 translate-y-0" : hidden[from]}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
}

/* ─── Shared UI ───────────────────────────────────────────── */
function Eyebrow({ text }: { text: string }) {
  return <span className="block text-[11px] font-bold tracking-[0.15em] uppercase text-blue-700 mb-3.5">{text}</span>;
}
function Divider() {
  return <div className="w-11 h-[3px] bg-blue-700 mb-6" />;
}
function CheckIcon() {
  return (
    <div className="w-5 h-5 bg-blue-700 flex-shrink-0 flex items-center justify-center mt-0.5">
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
        <path d="M1.5 5.5L3.8 7.8L8.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </div>
  );
}

/* ─── Data ────────────────────────────────────────────────── */
const SERVICES = [
  {
    n: "01", title: "Custom Website Development",
    body: "Fully bespoke websites designed from scratch around your brand identity and business objectives. Responsive design, custom functionality, SEO-friendly architecture and CMS integration.",
    img: "https://images.unsplash.com/photo-1547658719-da2b51169166?w=600&q=80",
    tags: ["Bespoke design", "SEO-friendly", "CMS integration"],
  },
  {
    n: "02", title: "E-commerce Development",
    body: "Feature-rich online stores with secure payment gateway integration, product management, inventory tracking, order management and multi-currency support. WooCommerce, Shopify, Magento, or custom.",
    img: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&q=80",
    tags: ["WooCommerce / Shopify", "Payment gateway", "Multi-currency"],
  },
  {
    n: "03", title: "CMS Development",
    body: "Flexible content management systems that empower your team to update content without developer involvement. WordPress, Strapi, Contentful, or fully custom CMS solutions.",
    img: "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?w=600&q=80",
    tags: ["WordPress", "Strapi / Contentful", "No-code editing"],
  },
  {
    n: "04", title: "Responsive Web Design",
    body: "Pixel-perfect designs that deliver a seamless experience across all screen sizes — desktop, tablet and mobile. Mobile-first approach with 99+ PageSpeed scores.",
    img: "https://images.unsplash.com/photo-1561070791-2526d30994b5?w=600&q=80",
    tags: ["Mobile-first", "99+ PageSpeed", "All screen sizes"],
  },
  {
    n: "05", title: "Website Redesign",
    body: "Transform your outdated website into a modern, high-performing digital asset. UI/UX improvements, performance optimisation, accessibility compliance and content restructuring.",
    img: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&q=80",
    tags: ["UI/UX refresh", "Performance lift", "WCAG 2.1 AA"],
  },
  {
    n: "06", title: "Maintenance & Support",
    body: "Comprehensive website maintenance — security updates, performance monitoring, content updates, backups, uptime monitoring and emergency support when you need it.",
    img: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=600&q=80",
    tags: ["Security patches", "Uptime monitoring", "Emergency support"],
  },
];

const TECH_STACK = [
  { layer: "Frontend",      tech: "React, Next.js, Vue.js, Angular, HTML5, CSS3, SCSS" },
  { layer: "Backend",       tech: "Node.js, PHP, Python, Laravel, Django, Express" },
  { layer: "CMS Platforms", tech: "WordPress, Shopify, Magento, Strapi, Contentful" },
  { layer: "Databases",     tech: "MySQL, PostgreSQL, MongoDB, Firebase" },
  { layer: "Performance",   tech: "CDN integration, image optimisation, lazy loading, caching" },
  { layer: "SEO",           tech: "Schema markup, Core Web Vitals, sitemap, robots.txt, meta optimisation" },
];

function TechFeatureRow({ row, index }: { row: (typeof TECH_STACK)[number]; index: number }) {
  const [ref, v] = useVisible<HTMLDivElement>();
  return (
    <div
      ref={ref}
      className={`grid grid-cols-[1fr_1.8fr] gap-4 px-4 py-3.5 border-b border-slate-200 items-center transition-all duration-500 ${v ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-3"} ${index % 2 === 0 ? "bg-white" : "bg-slate-50"}`}
      style={{ transitionDelay: `${(index % 4) * 0.07}s` }}
    >
      <span className="text-[13px] font-bold text-slate-900">{row.layer}</span>
      <span className="text-[13px] text-slate-500 leading-relaxed">{row.tech}</span>
    </div>
  );
}

const PERF_STANDARDS = [
  "Google PageSpeed Score: 90+ on desktop, 80+ on mobile",
  "Core Web Vitals: LCP < 2.5s, CLS < 0.1, FID < 100ms",
  "SSL secured (HTTPS) on all websites as standard",
  "Accessibility: WCAG 2.1 AA compliance on every build",
  "Cross-browser: Chrome, Safari, Firefox, Edge — all versions",
];

const STEPS = [
  { n: "01", title: "Requirements & Discovery",  body: "Define goals, target audience, feature set and design direction through structured discovery workshops." },
  { n: "02", title: "UI/UX Design",              body: "Wireframes, design system and high-fidelity mockups reviewed and approved before any development begins." },
  { n: "03", title: "Development",               body: "Clean, scalable, well-documented code built with modern frameworks — responsive from the ground up." },
  { n: "04", title: "Testing & QA",              body: "Cross-device, cross-browser testing, performance benchmarking, accessibility audits and security scans." },
  { n: "05", title: "SEO & Performance",         body: "On-page SEO implementation, Core Web Vitals optimisation, schema markup and PageSpeed tuning before launch." },
  { n: "06", title: "Launch & Ongoing Support",  body: "Managed deployment, DNS setup, uptime monitoring and a post-launch support window included as standard." },
];

const FAQS = [
  { q: "How much does a website cost?",          a: "Simple brochure websites start from ₹75,000. E-commerce platforms range from ₹2–10 lakh. Custom web applications start from ₹10 lakh. We provide a detailed quote after a free requirements call." },
  { q: "How long does website development take?",a: "Simple sites typically take 4–8 weeks. Mid-complexity sites with CMS: 8–16 weeks. Complex e-commerce or web applications: 16+ weeks. We provide a detailed timeline after the discovery phase." },
  { q: "Will my website be mobile-friendly?",    a: "Yes. We build mobile-first — every website is fully responsive and tested across devices and screen sizes before launch. We target 80+ PageSpeed on mobile." },
  { q: "Can you help with SEO?",                 a: "Yes. SEO-friendly architecture, Core Web Vitals optimisation, schema markup, meta tags and sitemap submission are included in every build. We also offer ongoing SEO services." },
  { q: "Do you integrate payment gateways?",     a: "Yes. We integrate Razorpay, PayU, Stripe, PhonePe and other major gateways for e-commerce and subscription-based platforms." },
  { q: "Do you provide ongoing maintenance?",    a: "Yes. We offer post-launch maintenance packages covering security updates, backups, performance monitoring, content updates and emergency support." },
];

/* ─── FAQ Item ────────────────────────────────────────────── */
function FaqItem({ q, a, i }: { q: string; a: string; i: number }) {
  const [open, setOpen] = useState(false);
  const [ref, v] = useVisible();
  return (
    <div
      ref={ref}
      className={`border-b border-slate-200 transition-all duration-500 ${v ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
      style={{ transitionDelay: `${i * 0.06}s` }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left flex justify-between items-center gap-4 py-[22px] bg-transparent border-none cursor-pointer"
      >
        <span className="text-[15px] font-semibold text-slate-900 leading-snug">{q}</span>
        <div className={`w-7 h-7 flex-shrink-0 flex items-center justify-center transition-colors duration-200 ${open ? "bg-blue-700" : "bg-slate-100"}`}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d={open ? "M2 6h8" : "M6 2v8M2 6h8"} stroke={open ? "#fff" : "#1565C0"} strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </div>
      </button>
      {open && (
        <div className="pb-[22px]">
          <p className="text-[14px] text-slate-500 leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
}

/* ─── Service Card ────────────────────────────────────────── */
function ServiceCard(props: { s: { tags: string[]; img: string; title: string; body: string; n: string }; i: number }) {
  const { s, i } = props;
  const [ref, v] = useVisible();
  const [hov, setHov] = useState(false);
  return (
    <div
      ref={ref}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      className={`flex flex-col overflow-hidden bg-white border transition-all duration-500 ${hov ? "border-slate-300 shadow-xl" : "border-slate-200 shadow-sm"}`}
      style={{ transitionDelay: `${(i % 3) * 0.09}s`, opacity: v ? 1 : 0, transform: v ? "none" : "translateY(24px)" }}
    >
      <div className="relative h-[170px] overflow-hidden">
        <img
          src={s.img} alt={s.title}
          className={`w-full h-full object-cover transition-transform duration-500 ${hov ? "scale-105" : "scale-100"}`}
        />
        <div className="absolute top-3.5 left-3.5 bg-blue-700 text-white text-[11px] font-bold tracking-[0.08em] px-2.5 py-1">
          {s.n}
        </div>
      </div>
      <div className="p-6 flex flex-col flex-1">
        <p className="text-[14px] font-bold text-slate-900 mb-2.5 leading-snug">{s.title}</p>
        <p className="text-[13.5px] text-slate-500 leading-relaxed mb-4">{s.body}</p>
        <div className="flex flex-wrap gap-1.5 mt-auto">
          {s.tags.map(tag => (
            <span key={tag} className="text-[11px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 tracking-wide">{tag}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Step ────────────────────────────────────────────────── */
function Step(props: { s: { n: string; title: string; body: string }; i: number; last?: boolean }) {
  const { s, i, last } = props;
  const [ref, v] = useVisible();
  return (
    <div
      ref={ref}
      className={`flex gap-4 items-start relative transition-all duration-500 ${!last ? "pb-7" : ""}`}
      style={{ transitionDelay: `${(i % 3) * 0.09}s`, opacity: v ? 1 : 0, transform: v ? "none" : "translateY(20px)" }}
    >
      {!last && <div className="absolute left-[17px] top-9 w-px bottom-0 bg-slate-200" />}
      <div className="w-9 h-9 bg-blue-700 flex-shrink-0 flex items-center justify-center">
        <span className="text-[11px] font-bold text-white">{s.n}</span>
      </div>
      <div className="pt-1.5">
        <p className="text-[14px] font-bold text-slate-900 mb-1.5">{s.title}</p>
        <p className="text-[13.5px] text-slate-500 leading-relaxed">{s.body}</p>
      </div>
    </div>
  );
}

/* ─── Page ────────────────────────────────────────────────── */
export default function WebDevPage() {
  return (
    <main className="bg-white">

      {/* ══ HERO ═══════════════════════════════════════════ */}
      <section className="grid grid-cols-1 lg:grid-cols-2 min-h-0 lg:min-h-[580px]">
        <div className="flex flex-col justify-center px-4 sm:px-8 lg:px-20 py-[30px] bg-white">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-slate-400 mb-6">
            <Link href="/services" className="text-blue-700 font-medium hover:underline">Services</Link>
            <span className="text-slate-300">›</span>
            <Link href="/services/it" className="text-blue-700 font-medium hover:underline">IT Solutions</Link>
            <span className="text-slate-300">›</span>
            <span>Web Development</span>
          </nav>

          {/* Badge tags */}
          <div className="flex flex-wrap gap-2 mb-5">
            {["Custom Websites", "E-commerce", "CMS", "Next.js / React", "SEO"].map(tag => (
              <span key={tag} className="text-[11px] font-semibold text-blue-700 bg-blue-50 px-2.5 py-1 tracking-wide">{tag}</span>
            ))}
          </div>

          <Eyebrow text="IT Solutions — Web Development" />
          <Divider />

          <h1 className="text-[clamp(26px,3.5vw,46px)] font-extrabold text-slate-900 leading-[1.1] mb-5 ">
            Websites That Don&apos;t Just Look Good — They Perform
          </h1>
          <p className="text-[16px] font-light text-slate-500 leading-relaxed mb-9 ">
            We design and build stunning, conversion-optimised websites for businesses of all sizes — from corporate sites to complex e-commerce platforms — that load fast, rank high and turn visitors into customers.
          </p>
          <div className="flex gap-3.5">
            <button className="bg-blue-700 text-white text-[13px] font-bold tracking-[0.06em] uppercase px-7 py-3.5 hover:bg-blue-800 transition-colors border-0 cursor-pointer">
              START YOUR PROJECT
            </button>
            <button className="bg-transparent text-slate-900 text-[13px] font-semibold tracking-[0.06em] uppercase px-7 py-3.5 border border-slate-200 hover:border-slate-400 transition-colors cursor-pointer">
              GET A FREE QUOTE
            </button>
          </div>
        </div>

        {/* Right: hero photo */}
        <div className="relative overflow-hidden">
          <img
            src="/images/services/IT/Banner/WebDesign.png"
            alt="Web development"
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* Blue stat strip */}
      <div className="bg-blue-700 grid grid-cols-2 lg:grid-cols-4">
        {[
          { v: "90+",    l: "PageSpeed Score" },
          { v: "6",      l: "Service Areas" },
          { v: "4 Wks",  l: "Fastest Delivery" },
          { v: "WCAG",   l: "2.1 AA Compliant" },
        ].map((s, i) => (
          <div key={i} className={`py-5 text-center ${i < 3 ? "border-r border-white/15" : ""}`}>
            <p className="text-[22px] font-black text-white leading-none">{s.v}</p>
            <p className="text-[11px] font-medium text-white/65 mt-1.5 tracking-widest uppercase">{s.l}</p>
          </div>
        ))}
      </div>

      {/* ══ SERVICES GRID ══════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-[1160px] mx-auto">
          <Reveal>
            <div className="mb-10 lg:mb-14">
              <Eyebrow text="Web Development Services" />
              <Divider />
              <div className="flex justify-between items-end flex-wrap gap-4">
                <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight m-0">
                  Six Capabilities.<br />One Expert Team.
                </h2>
                <p className="text-[14px] text-slate-500 max-w-[360px] leading-relaxed">
                  From first wireframe to post-launch support — complete web development under one roof.
                </p>
              </div>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s, i) => <ServiceCard key={i} s={s} i={i} />)}
          </div>
        </div>
      </section>

      {/* ══ TECH STACK + PERFORMANCE STANDARDS ════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-start">

          {/* Left: tech stack table */}
          <Reveal from="left">
            <Eyebrow text="Technology Stack" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              Modern. Proven. Maintainable.
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-8">
              We select the best tools for each project — ensuring your website is fast, scalable and easy to update.
            </p>

            {/* Table */}
            <div>
              <div className="grid grid-cols-[1fr_1.8fr] bg-blue-700 px-4 py-3">
                <span className="text-[11px] font-bold text-white tracking-[0.1em] uppercase">Layer</span>
                <span className="text-[11px] font-bold text-white tracking-[0.1em] uppercase">Technologies</span>
              </div>
              {TECH_STACK.map((row, i) => (
                <TechFeatureRow key={i} row={row} index={i} />
              ))}
            </div>
          </Reveal>

          {/* Right: performance standards + image */}
          <Reveal from="right" delay={0.12}>
            <div className="relative mb-10">
              <img
                src="https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?w=700&q=80"
                alt="Web performance"
                className="w-full h-[200px] sm:h-[260px] lg:h-[320px] object-cover"
              />
              {/* Floating stat */}
              <div className="absolute bottom-[-24px] right-[-20px] bg-white border border-slate-200 shadow-xl px-6 py-5 hidden lg:block">
                <p className="text-[30px] font-black text-blue-700 leading-none">90+</p>
                <p className="text-[11px] text-slate-400 mt-1.5 tracking-[0.06em] uppercase font-medium">PageSpeed Score</p>
                <p className="text-[28px] font-black text-orange-600 leading-none mt-4">4 Wks</p>
                <p className="text-[11px] text-slate-400 mt-1.5 tracking-[0.06em] uppercase font-medium">Fastest Delivery</p>
              </div>
            </div>

            <div className="mt-10 pt-8">
              <p className="text-[12px] font-bold text-slate-400 tracking-[0.1em] uppercase mb-3">Performance Standards</p>
              <div className="border-t-2 border-blue-700">
                {PERF_STANDARDS.map((pt, i) => (
                  <div key={i} className={`flex items-start gap-3 py-3.5 ${i < PERF_STANDARDS.length - 1 ? "border-b border-slate-100" : ""}`}>
                    <CheckIcon />
                    <span className="text-[13.5px] text-slate-600 leading-relaxed">{pt}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ 6-STEP PROCESS ═════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-start">

          <Reveal from="left">
            <Eyebrow text="Development Process" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              6-Step Web Development Process
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-10">
              A transparent, milestone-driven process — with client sign-off at every stage and no surprises at launch.
            </p>
            <img
              src="https://images.unsplash.com/photo-1561070791-2526d30994b5?w=700&q=80"
              alt="Web development process"
              className="w-full h-[180px] sm:h-[220px] lg:h-[260px] object-cover"
            />
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div>
              {STEPS.map((s, i) => <Step key={i} s={s} i={i} last={i === STEPS.length - 1} />)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ FAQ ════════════════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-start">

          <Reveal from="left">
            <Eyebrow text="Frequently Asked Questions" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,36px)] font-bold text-slate-900 leading-tight mb-6">
              Common Questions About Web Development
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-9">
              Clear answers to the questions we hear most from businesses planning a new website or e-commerce platform.
            </p>
            <img
              src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=700&q=80"
              alt="Web development FAQ"
              className="w-full h-[180px] sm:h-[240px] lg:h-[280px] object-cover"
            />
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div className="border-t border-slate-200">
              {FAQS.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} i={i} />)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ CTA ════════════════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center">

          <Reveal from="left">
            <video
              src="/videos/CTA.mp4"
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-[220px] sm:h-[300px] lg:h-[380px] object-cover"
            />
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <Eyebrow text="Ready to Build Your Online Presence?" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              Start Your Web Project Today
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-3">
              Tell us about your website — what you need, who you&apos;re targeting and what success looks like. We&apos;ll provide a clear scope and proposal within 48 hours.
            </p>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-9">
              Fixed-price delivery, milestone-based payments and a post-launch support window — all included as standard.
            </p>
            <div className="flex gap-3.5 flex-wrap">
              <button className="bg-blue-700 text-white text-[13px] font-bold tracking-[0.06em] uppercase px-8 py-3.5 hover:bg-blue-800 transition-colors border-0 cursor-pointer">
                START YOUR PROJECT
              </button>
              <button className="bg-transparent text-slate-900 text-[13px] font-semibold tracking-[0.06em] uppercase px-8 py-3.5 border border-slate-200 hover:border-slate-400 transition-colors cursor-pointer">
                GET A FREE QUOTE
              </button>
            </div>
          </Reveal>
        </div>
      </section>

    </main>
  );
}